/**
 * @voltxampere/web — `<vxa-export-dialog>` (Sprint 16)
 *
 * Modal SPICE netlist exporter. Auto-runs `toSpice()` on open and
 * presents the canonical Berkeley SPICE3 output ready to copy or
 * download as `.cir`.
 *
 * Sprint 14 D5 disiplini: every action wired through `data-action`
 * + parent `@click` listener. Sprint 11.0.1 composedPath guard
 * re-applied to keep clicks inside the dialog scoped properly.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import {
  exportDialogOpen,
  closeExportDialog,
  exportCurrentCircuit,
  copySpiceToClipboard,
  downloadSpice,
  type ExportPayload,
} from "../store/import-export.js";
import { t } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";

@customElement("vxa-export-dialog")
export class VxaExportDialog extends LitElement {
  private readonly _open = new SignalController(this, exportDialogOpen);

  @state() private accessor payload: ExportPayload | null = null;
  @state() private accessor copyConfirm = false;

  static override styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: 100;
      display: none;
    }

    :host([data-open]) {
      display: grid;
      place-items: center;
      background: rgba(7, 7, 9, 0.78);
      backdrop-filter: blur(4px);
    }

    .panel {
      width: min(720px, 92vw);
      max-height: 86vh;
      background: var(--ink);
      border: 1px solid var(--brass-faint);
      border-radius: 12px;
      box-shadow:
        0 24px 56px rgba(0, 0, 0, 0.55),
        0 0 0 1px var(--brass-faint);
      display: grid;
      grid-template-rows: auto 1fr auto;
      overflow: hidden;
    }

    .header {
      padding: var(--s-5) var(--s-6);
      border-bottom: 1px solid var(--ivory-wisp);
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .title {
      font-family: var(--font-display);
      font-size: 22px;
      letter-spacing: -0.01em;
      color: var(--ivory);
    }

    .close {
      background: none;
      border: 0;
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.22em;
      color: var(--ivory-dim);
      cursor: pointer;
      padding: 6px 12px;
    }

    .close:hover {
      color: var(--brass);
    }

    .body {
      padding: var(--s-5) var(--s-6);
      display: grid;
      gap: var(--s-3);
      overflow-y: auto;
    }

    .section-label {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.18em;
      color: var(--ivory-dim);
      text-transform: uppercase;
    }

    pre.output {
      width: 100%;
      min-height: 240px;
      max-height: 380px;
      overflow: auto;
      font-family: var(--font-mono);
      font-size: 13px;
      line-height: 1.55;
      color: var(--ivory);
      background: var(--void);
      border: 1px solid var(--ivory-wisp);
      border-left: 3px solid var(--brass-faint);
      padding: 12px 14px;
      margin: 0;
      white-space: pre;
      box-sizing: border-box;
    }

    .empty-state {
      padding: var(--s-5);
      text-align: center;
      color: var(--ivory-dim);
      font-family: var(--font-body);
      font-style: italic;
    }

    .footer {
      padding: var(--s-4) var(--s-6);
      border-top: 1px solid var(--ivory-wisp);
      display: flex;
      gap: var(--s-3);
      justify-content: flex-end;
    }

    button.action {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.22em;
      padding: 8px 18px;
      border: 1px solid var(--brass-faint);
      background: transparent;
      color: var(--ivory-dim);
      cursor: pointer;
      transition:
        border-color var(--transition-hover),
        background var(--transition-hover),
        color var(--transition-hover);
    }

    button.action:hover {
      color: var(--brass);
      border-color: var(--brass);
    }

    button.action.primary {
      color: var(--brass);
      border-color: var(--brass);
      background: var(--brass-ghost);
    }

    button.action.primary:hover {
      color: var(--brass-hot);
      background: rgba(196, 145, 89, 0.18);
    }

    button.action[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `;

  override updated(changed: Map<string, unknown>): void {
    this.toggleAttribute("data-open", this._open.value);
    if (changed.has("_open") || (this._open.value && this.payload === null)) {
      // Refresh payload whenever the dialog opens.
      if (this._open.value) {
        this.payload = exportCurrentCircuit();
        this.copyConfirm = false;
      }
    }
  }

  override render() {
    if (!this._open.value) return nothing;
    // Re-snapshot if needed (handles signal updates while dialog open).
    this.payload ??= exportCurrentCircuit();
    return html`
      <div class="panel" @click=${this.handleAction}>
        <div class="header">
          <div class="title">${t("export.title")}</div>
          <button
            class="close"
            data-action="close"
            type="button"
            aria-label=${t("modal.close")}
            title=${t("modal.close") + " (Esc)"}
          >
            ${t("modal.close")} <span aria-hidden="true">✕</span>
          </button>
        </div>
        <div class="body">${this.renderBody()}</div>
        <div class="footer">
          <button class="action" data-action="close" type="button">
            ${t("export.button.close")}
          </button>
          <button
            class="action"
            data-action="copy"
            type="button"
            ?disabled=${this.payload === null}
          >
            ${this.copyConfirm ? t("export.button.copied") : t("export.button.copy")}
          </button>
          <button
            class="action primary"
            data-action="download"
            type="button"
            ?disabled=${this.payload === null}
          >
            ${t("export.button.download")}
          </button>
        </div>
      </div>
    `;
  }

  private renderBody() {
    if (this.payload === null) {
      return html`<div class="empty-state">${t("export.empty")}</div>`;
    }
    return html`
      <div class="section-label">${t("export.preview.label")}</div>
      <pre class="output">${this.payload.text}</pre>
    `;
  }

  private handleAction = (e: Event): void => {
    const path = e.composedPath();
    const target = path.find(
      (n): n is HTMLElement => n instanceof HTMLElement && typeof n.dataset.action === "string",
    );
    if (!target) return;
    const action = target.dataset.action;
    switch (action) {
      case "close":
        this.close();
        break;
      case "copy":
        void this.copy();
        break;
      case "download":
        this.download();
        break;
      default:
        break;
    }
  };

  private close(): void {
    this.payload = null;
    this.copyConfirm = false;
    closeExportDialog();
  }

  private async copy(): Promise<void> {
    if (this.payload === null) return;
    const ok = await copySpiceToClipboard(this.payload);
    if (ok) {
      this.copyConfirm = true;
      setTimeout(() => {
        this.copyConfirm = false;
      }, 1500);
    }
  }

  private download(): void {
    if (this.payload === null) return;
    downloadSpice(this.payload);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-export-dialog": VxaExportDialog;
  }
}
